import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CreditCard, ShieldCheck } from "lucide-react";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [loading, setLoading] = useState(false);
  const [paypal, setPaypal] = useState({ enabled: false, client_id: "" });

  useEffect(() => {
    if (items.length === 0) navigate("/cart");
    api.get("/config/paypal").then((r) => setPaypal(r.data)).catch(() => {});
  }, [items.length, navigate]);

  const payload = () => ({
    items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size })),
    origin_url: window.location.origin,
    customer_email: email,
    customer_name: name,
  });

  const validate = () => {
    if (!name || !email) { toast.error("Please enter your name and email"); return false; }
    return true;
  };

  const payWithStripe = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/stripe", payload());
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-heading text-5xl font-900 uppercase tracking-tight mb-10">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Details + payment */}
        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-4">Contact Details</h3>
          <div className="space-y-4">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="checkout-name"
              className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white placeholder-[#52525B] focus:outline-none focus:border-[#E63946] transition-colors" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" type="email" data-testid="checkout-email"
              className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white placeholder-[#52525B] focus:outline-none focus:border-[#E63946] transition-colors" />
          </div>

          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mt-8 mb-4">Payment Method</h3>

          <button onClick={payWithStripe} disabled={loading} data-testid="pay-stripe-btn"
            className="w-full bg-[#E63946] hover:bg-[#FF4D5A] disabled:opacity-60 text-white font-semibold uppercase tracking-wider text-sm px-6 py-4 flex items-center justify-center gap-2 transition-colors">
            <CreditCard size={18} /> {loading ? "Redirecting…" : "Card · Apple Pay · Google Pay"}
          </button>
          <p className="text-xs text-[#52525B] mt-2">Secure checkout powered by Stripe.</p>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px bg-white/10 flex-1" />
            <span className="text-xs text-[#52525B] uppercase tracking-wider">or</span>
            <div className="h-px bg-white/10 flex-1" />
          </div>

          {paypal.enabled ? (
            <div data-testid="paypal-container">
              <PayPalScriptProvider options={{ clientId: paypal.client_id, currency: "USD", "enable-funding": "venmo" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "rect" }}
                  disabled={loading}
                  createOrder={async () => {
                    if (!validate()) throw new Error("missing details");
                    const { data } = await api.post("/checkout/paypal/create", payload());
                    return data.order_id;
                  }}
                  onApprove={async (data) => {
                    const res = await api.post(`/checkout/paypal/capture/${data.orderID}`);
                    if (res.data.status === "COMPLETED") {
                      clear();
                      navigate(`/checkout/success?order_id=${data.orderID}&method=paypal`);
                    } else {
                      toast.error("PayPal payment not completed.");
                    }
                  }}
                  onError={() => toast.error("PayPal error. Please try another method.")}
                />
              </PayPalScriptProvider>
            </div>
          ) : (
            <div className="border border-white/10 bg-[#121212] p-4 text-sm text-[#A1A1AA]" data-testid="paypal-disabled">
              PayPal &amp; Venmo will appear here once PayPal API keys are added.
            </div>
          )}
        </div>

        {/* Order summary */}
        <div className="bg-[#121212] border border-white/10 p-6 h-fit">
          <h3 className="font-heading text-xl font-700 uppercase tracking-tight mb-4">Order Summary</h3>
          <div className="space-y-3 mb-4">
            {items.map((i) => (
              <div key={i.key} className="flex justify-between text-sm">
                <span className="text-[#A1A1AA]">{i.name}{i.size ? ` (${i.size})` : ""} × {i.quantity}</span>
                <span>${(i.price * i.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-4 flex justify-between font-heading font-800 text-xl">
            <span>Total</span><span data-testid="checkout-total">${total.toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-2 mt-6 text-xs text-[#52525B]">
            <ShieldCheck size={14} /> Encrypted &amp; secure payments
          </div>
        </div>
      </div>
    </div>
  );
}
